import { useEffect, useState } from 'react';
import { Layers } from 'lucide-react';
import {
  creativeWorkbenchApi,
  type WorkbenchFormulaResponse,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import type { ChatSeedInput } from './EmbeddedChatPanel';

interface Props {
  onDrillDown: (seed: ChatSeedInput) => void;
  limit?: number;
}

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function FormulaAffinityPanel({ onDrillDown, limit = 4 }: Props) {
  const { clientUid, personaId } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchFormulaResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getFormulaAffinity({ clientUid, personaId, limit })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, personaId, limit]);

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Layers size={14} className="text-teal-500" />
          Top Formulas by Persona
        </h2>
        <span className="text-[10px] font-mono text-slate-400">
          hook · tone · visual
        </span>
      </header>

      {loading && !data && (
        <div className="text-xs text-slate-400">Loading...</div>
      )}

      {data && data.personas.length === 0 && !loading && (
        <div className="text-xs text-slate-400">
          No formula attribution yet.
        </div>
      )}

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {(data?.personas ?? []).map((p) => (
          <div key={p.persona_name} className="space-y-1.5">
            <div className="flex items-center justify-between text-[11px] text-slate-700 font-bold border-b border-slate-100 pb-1">
              <span className="truncate" title={p.persona_name}>
                {p.persona_name}
              </span>
              <span className="text-[10px] font-mono text-slate-400">
                {p.formulas.length} formulas
              </span>
            </div>
            <ul className="space-y-1">
              {p.formulas.map((f) => (
                <li
                  key={f.formula}
                  className="flex items-center justify-between text-[11px] gap-2 group"
                >
                  <button
                    onClick={() =>
                      onDrillDown({
                        question: `Why does ${p.persona_name} respond to "${f.formula}" creative?`,
                        chart_axis: {
                          series: 'formula',
                          dimension: 'persona',
                          formula: f.formula,
                          persona: p.persona_name,
                        },
                      })
                    }
                    className="flex-1 min-w-0 text-left text-slate-700 hover:text-teal-600 truncate"
                    title={f.formula}
                  >
                    {f.formula}
                  </button>
                  <div className="flex items-center gap-3 text-right text-[10px] font-mono">
                    <span className="text-slate-500">
                      {f.visitors.toLocaleString()} visit
                    </span>
                    <span className="text-teal-600 font-bold">
                      {CURRENCY.format(f.estimated_value)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
